import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Badge, Popover, SpaceBetween, Box, Spinner, Link, Container } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { useNotificationBellStore } from '../../store/notification-bell-store';
import { formatDistanceToNow } from 'date-fns';
import './NotificationBell.scss';
import { logger } from '../../utils/logger';

// Get category badge CSS class
const getCategoryBadgeClass = (category: any): string => {
  const lowerCategory = category.toLowerCase();
  if (lowerCategory === 'mfr') return 'notification-item__category-badge--mfr';
  if (lowerCategory === 'netppm') return 'notification-item__category-badge--netppm';
  if (lowerCategory === 'rvm') return 'notification-item__category-badge--rvm';
  return 'notification-item__category-badge--default';
};

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadInitialNotifications,
    loadMoreNotifications,
    markAsSeen,
  } = useNotificationBellStore();

  // Load notifications on mount
  useEffect(() => {
    loadInitialNotifications();
    
    // Cleanup: stop polling when component unmounts
    return () => {
      useNotificationBellStore.getState().stopPolling();
    };
  }, [loadInitialNotifications]);

  // Handle clicks outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        bellRef.current &&
        popoverRef.current &&
        !bellRef.current.contains(event.target as Node) &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Add listener after a small delay to avoid closing on the same click that opened it
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle scroll for infinite loading
  const handleScroll = () => {
    if (!scrollRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    
    // Load more when scrolled to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreNotifications();
    }
  };

  // Handle bell button click to toggle popover
  const handleBellClick = () => {
    if (!isOpen) {
      // Opening popover - mark all unread notifications as seen
      const unreadIds = notifications
        .filter(n => !n.seenAt && n.id)
        .map(n => n.id!);
      
      if (unreadIds.length > 0) {
        logger.info('Marking all unread notifications as seen:', { unreadIds });
        markAsSeen(unreadIds);
      }
    }
    setIsOpen(!isOpen);
  };


  const renderNotificationItem = (notification: typeof notifications[0]) => {
    const isUnread = !notification.seenAt;
    const timeAgo = notification.createdAt 
      ? formatDistanceToNow(notification.createdAt, { addSuffix: true })
      : 'recently';
    
    // Extract category from notification content or metadata
    const category = notification.type;
    const categoryBadgeClass = getCategoryBadgeClass(category);

    return (
      <div
        key={notification.id}
        className={`notification-item ${isUnread ? 'notification-item--unread' : 'notification-item--read'}`}
        data-testid={`notification-${notification.id}`}
      >
        <SpaceBetween size="xs">
          {/* Category Badge */}
          <div>
            <span className={`notification-item__category-badge ${categoryBadgeClass}`}>
              {category}
            </span>
          </div>
          
          {/* Notification Content */}
          <Box fontSize="body-s">
            {notification.content}
          </Box>
          
          {/* Time and View Action */}
          <div className="notification-item__footer">
            <Box fontSize="body-s" color="text-status-inactive">
              <small>{timeAgo}</small>
            </Box>
            {notification.targetUrl && (
              <Link
                fontSize="body-s"
                href={notification.targetUrl}
                external={true}
                onFollow={(event) => {
                  // Prevent default Link behavior since we're handling navigation manually
                  event.preventDefault();
                  
                  // Close popover
                  setIsOpen(false);
                  
                  // Open in new tab
                  if (notification.targetUrl) {
                    window.open(notification.targetUrl, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                View
              </Link>
            )}
          </div>
        </SpaceBetween>
        
        {/* Unread Indicator - Red Dot */}
        {isUnread && (
          <div
            className="notification-item__unread-dot"
            data-testid={`notification-unread-indicator-${notification.id}`}
          />
        )}
      </div>
    );
  };

  const popoverContent = (
    <Container
      fitHeight
      disableContentPaddings
    >
      <div className="notification-popover__container">
        {/* Header */}
        <div className="notification-popover__header">
          <Box fontSize="heading-m" fontWeight="bold">
            Notifications
          </Box>
        </div>
        
        {/* Notification List */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="notification-popover__scroll-area"
        >
          {notifications.length === 0 && !isLoading ? (
            <Box padding="l" textAlign="center" color="text-status-inactive">
              No notifications
            </Box>
          ) : (
            <div>
              {notifications.map(renderNotificationItem)}
              {isLoading && (
                <Box padding="s" textAlign="center">
                  <Spinner />
                </Box>
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  );

  return (
    <>
      <div className="notification-bell__wrapper" ref={bellRef}>
        <Button
          variant="icon"
          iconName="notification"
          ariaLabel={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          onClick={handleBellClick}
          data-testid="notification-bell"
        />
        {unreadCount > 0 && (
          <div
            className="notification-bell__badge"
            data-testid="notification-badge"
            title={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
          />
        )}
      </div>
      
      {isOpen && (
        <div className="notification-popover__overlay">
          <div
            ref={popoverRef}
            className="notification-popover__content"
          >
            {popoverContent}
          </div>
        </div>
      )}
    </>
  );
};
